/**
 * Sticky Note Node Renderer
 *
 * Renders sticky-note nodes as colored square notes with auto-scaling text.
 * Always renders at full detail (no LOD).
 */

import type { UINode, ScreenMetrics } from '../types';
import type { NodeRenderer, RenderLayers, RenderResult } from '../renderer';
import type { MeasureOptions } from '../content-measure';

/**
 * Compute font size that fits text within a sticky note of given dimensions.
 *
 * Takes the minimum of:
 * - Width-proportional size (14% of width, like the old 14cqi)
 * - Content-fitting size (derived from available area / character count)
 *
 * Result is clamped to [MIN_FONT_SIZE, 72px].
 */
export const MIN_STICKY_FONT_SIZE = 8;

export function computeStickyNoteFontSize(
	width: number,
	height: number,
	content: string,
): number {
	const w = width;
	const h = height;

	// Width-proportional (equivalent to 14cqi)
	const widthBased = w * 0.14;

	if (!content || content.trim().length === 0) {
		return Math.max(MIN_STICKY_FONT_SIZE, Math.min(widthBased, 72));
	}

	// Available text area after padding (16px all sides)
	const availW = Math.max(1, w - 32);
	const availH = Math.max(1, h - 32);

	// Estimate fitting font size from area and character count.
	// Average char width ≈ 0.55 * fontSize, line height = 1.3 * fontSize.
	// chars_that_fit = (availW / (0.55*F)) * (availH / (1.3*F))
	// Solve for F: F = sqrt(availW * availH / (charCount * 0.715))
	const charCount = content.length;
	const contentBased = Math.sqrt((availW * availH) / (charCount * 0.715));

	const fontSize = Math.min(widthBased, contentBased);
	return Math.max(MIN_STICKY_FONT_SIZE, Math.min(fontSize, 72));
}

/**
 * Sticky note renderer for colored square notes.
 */
/** Default sticky note background (matches CSS) */
const DEFAULT_STICKY_COLOR = '#fff9b1';

/** Allowed sticky note colors. Any color not in this set falls back to default. */
const ALLOWED_STICKY_COLORS = new Set([
	'#fff9b1', // yellow (default)
	'#ffe066', // amber
	'#f3b8b8', // pink
	'#b8d4f3', // blue
	'#b8f3c8', // green
	'#e2b8f3', // purple
	'#f3d9b8', // peach
]);

/**
 * Cache key for shrinkToFit results.
 * The final font size is deterministic for a given (content, width, height) tuple
 * since the initial font size is derived from those same values.
 */
function shrinkToFitCacheKey(
	content: string,
	width: number,
	height: number,
): string {
	return `${width}|${height}|${content}`;
}

/** Cached result of shrinkToFit: final font size and whether overflow is needed */
interface ShrinkToFitResult {
	fontSize: number;
	overflow: boolean;
}

/** Max entries in the shrinkToFit cache before clearing old entries */
const MAX_SHRINK_CACHE_SIZE = 500;

export class StickyNoteRenderer implements NodeRenderer {
	private lastContent: Map<string, string> = new Map();
	private lastColor: Map<string, string> = new Map();
	/** Cache of shrinkToFit results keyed by (content, width, height) */
	private shrinkCache: Map<string, ShrinkToFitResult> = new Map();

	render(
		node: UINode,
		_metrics: ScreenMetrics,
		layers: RenderLayers,
	): RenderResult {
		const isNewElement = layers.html.isNewlyCreated(node.id);
		const content = this.renderContent(node);
		const lastContentValue = this.lastContent.get(node.id);
		const contentChanged = lastContentValue !== content || isNewElement;

		if (contentChanged) {
			layers.html.setContent(node.id, content);
			this.lastContent.set(node.id, content);
			// Only shrink-to-fit when autoSizeText is on (scaling font mode)
			if (this.hasAutoSizeText(node)) {
				this.shrinkToFit(node, layers);
			}
		}

		// Apply background color from data (validated against allowed palette)
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		const rawColor =
			typeof obj?.color === 'string' ? obj.color.toLowerCase() : '';
		const color = ALLOWED_STICKY_COLORS.has(rawColor)
			? rawColor
			: DEFAULT_STICKY_COLOR;
		const lastColorValue = this.lastColor.get(node.id);
		if (color !== lastColorValue || isNewElement) {
			const container = layers.html.getContainer(node.id);
			if (container) {
				if (color !== DEFAULT_STICKY_COLOR) {
					container.style.backgroundColor = color;
				} else {
					container.style.backgroundColor = '';
				}
			}
			this.lastColor.set(node.id, color);
		}

		return { lod: 'full', changed: contentChanged || color !== lastColorValue };
	}

	/**
	 * Post-render: if text overflows, find the largest font size that fits.
	 *
	 * Uses a cache keyed by (content, width, height) to avoid repeated binary
	 * searches. The binary search forces a synchronous layout reflow per
	 * iteration (~8-10 per call), which is the #1 cause of frame drops when
	 * many sticky notes become visible simultaneously (zoom out, pan, initial load).
	 *
	 * On cache hit: 1 style write (vs 8-10 write/read cycles).
	 * On cache miss: full binary search, result stored for future hits.
	 */
	private shrinkToFit(node: UINode, layers: RenderLayers): void {
		const container = layers.html.getContainer(node.id);
		const textEl = container?.querySelector(
			'.sticky-note-text',
		) as HTMLElement | null;
		if (!textEl) return;

		// Remove overflow class from any previous render
		textEl.classList.remove('sticky-note-overflow');

		const width = node.size?.width ?? 200;
		const height = node.size?.height ?? 200;
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		const content = this.getContent(obj);
		const cacheKey = shrinkToFitCacheKey(content, width, height);

		// Check cache first — avoids all layout thrashing
		const cached = this.shrinkCache.get(cacheKey);
		if (cached) {
			textEl.style.fontSize = `${cached.fontSize}px`;
			if (cached.overflow) {
				textEl.classList.add('sticky-note-overflow');
			}
			return;
		}

		// No cache hit — check if shrinking is even needed (1 layout read)
		if (textEl.scrollHeight <= textEl.clientHeight) return;

		// Current font size from inline style
		const currentSize = parseFloat(textEl.style.fontSize);
		if (!currentSize || currentSize <= MIN_STICKY_FONT_SIZE) {
			textEl.classList.add('sticky-note-overflow');
			if (this.shrinkCache.size >= MAX_SHRINK_CACHE_SIZE) {
				this.shrinkCache.clear();
			}
			this.shrinkCache.set(cacheKey, {
				fontSize: currentSize || MIN_STICKY_FONT_SIZE,
				overflow: true,
			});
			return;
		}

		// Binary search for largest font size that fits
		let lo = MIN_STICKY_FONT_SIZE;
		let hi = currentSize;
		while (hi - lo > 0.5) {
			const mid = (lo + hi) / 2;
			textEl.style.fontSize = `${mid}px`;
			if (textEl.scrollHeight > textEl.clientHeight) {
				hi = mid;
			} else {
				lo = mid;
			}
		}
		textEl.style.fontSize = `${lo}px`;

		const overflow = textEl.scrollHeight > textEl.clientHeight;
		if (overflow) {
			textEl.classList.add('sticky-note-overflow');
		}

		// Cache the result (evict all if cache grows too large)
		if (this.shrinkCache.size >= MAX_SHRINK_CACHE_SIZE) {
			this.shrinkCache.clear();
		}
		this.shrinkCache.set(cacheKey, { fontSize: lo, overflow });
	}

	private hasAutoSizeText(node: UINode): boolean {
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		if (typeof obj.autoSizeText === 'boolean') return obj.autoSizeText;
		return true; // sticky-note default: autoSizeText on
	}

	private hasWordWrap(node: UINode): boolean {
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		if (typeof obj.wordWrap === 'boolean') return obj.wordWrap;
		return true; // sticky-note default
	}

	private renderContent(node: UINode): string {
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		const content = this.getContent(obj);
		const isEmpty = !content || content.trim().length === 0;
		const displayText = isEmpty ? '' : this.escapeHtml(content);
		const autoSizeText = this.hasAutoSizeText(node);
		const wordWrap = this.hasWordWrap(node);
		const ws = wordWrap ? 'pre-wrap' : 'nowrap';
		const wb = wordWrap ? 'break-word' : 'normal';

		let fontSize: number;
		if (autoSizeText) {
			fontSize = this.computeFontSize(node, content);
		} else {
			// Fixed base font size, rely on autofit for sizing
			fontSize = 16;
		}

		return `
			<div class="sticky-note-node">
				<div class="sticky-note-close node-close-btn" data-action="close">&times;</div>
				<div class="sticky-note-text${isEmpty ? ' sticky-note-empty' : ''}" style="font-size: ${fontSize}px; white-space: ${ws}; word-break: ${wb}">${displayText}</div>
			</div>
		`;
	}

	private computeFontSize(node: UINode, content: string): number {
		return computeStickyNoteFontSize(
			node.size?.width ?? 200,
			node.size?.height ?? 200,
			content,
		);
	}

	private getContent(obj: Record<string, unknown>): string {
		return typeof obj?.content === 'string' ? (obj.content as string) : '';
	}

	private escapeHtml(text: string): string {
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	/**
	 * Content HTML for measurement (auto-fit sizing).
	 * Uses fixed base font size for deterministic measurement.
	 */
	getContentHTML(node: UINode): string {
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		const content = this.getContent(obj);
		const displayText = content ? this.escapeHtml(content) : '';
		const wordWrap = this.hasWordWrap(node);
		const ws = wordWrap ? 'pre-wrap' : 'nowrap';
		const wb = wordWrap ? 'break-word' : 'normal';
		return `<div class="sticky-note-text" style="font-size: 16px; white-space: ${ws}; word-break: ${wb}; overflow: visible; padding: 16px">${displayText}</div>`;
	}

	getMeasureOptions(_node: UINode): MeasureOptions {
		return { maxWidth: 400, minWidth: 100, minHeight: 100, padding: 0 };
	}

	getPreferredSize(node: UINode): { width: number; height: number } {
		return {
			width: node.size?.width ?? 200,
			height: node.size?.height ?? 200,
		};
	}

	cleanup(nodeId: string): void {
		this.lastContent.delete(nodeId);
		this.lastColor.delete(nodeId);
		// shrinkCache is keyed by (content, width, height) not nodeId,
		// so entries may benefit other nodes with the same content/size.
		// Bounded by MAX_SHRINK_CACHE_SIZE to prevent unbounded growth.
	}
}
